<template>
  <div class="weekly-view-wrapper">
    <div class="weekly-view-inner-wrapper">
      <div class="filter-controls-container pa-4">
        <v-row align="center" no-gutters>
          <v-col class="d-flex">
            <v-btn icon elevation="2" large style="background-color: white" @click="$emit('toDailyView')"><v-icon>mdi-chevron-left</v-icon></v-btn>
            <v-autocomplete
              class="ml-2"
              v-model="search"
              :items="searchItems"
              :disabled="isUpdating"
              :loading="loading"
              item-text="name"
              item-value="name"
              multiple
              chips
              deletable-chips
              outlined
              dense
              hide-details
              label="Kurse, Instruktoren filtern"
              @input="searchInput = null"
              :search-input.sync="searchInput">
              <template v-slot:selection="data">
                <v-chip v-bind="data.attrs" :input-value="data.selected" close small @click="data.select" @click:close="remove(data.item)">
                  <v-avatar left v-if="data.item.image">
                    <v-img :src="data.item.image"></v-img>
                  </v-avatar>
                  {{ data.item.name }}
                </v-chip>
              </template>
              <template v-slot:item="data">
                <template v-if="typeof data.item !== 'object'">
                  <v-list-item-content v-text="data.item"></v-list-item-content>
                </template>
                <template v-else>
                  <v-list-item-avatar>
                    <img v-if="data.item.image" :src="data.item.image" />
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title v-html="data.item.name"></v-list-item-title>
                  </v-list-item-content>
                </template>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="auto" class="pl-2">
            <v-btn large icon @click="downloadPrint" title="Wochenplan herunterladen">
              <v-icon>mdi-download</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <div v-if="loading" class="text-center py-16">
        <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
      </div>

      <div v-else>
        <div v-for="(courseDay, i) of courseListFiltered" :key="i + 'courseDay'">
          <template v-if="courseDay && courseDay.length > 0">
            <div class="day-divider">
              <span class="day-chip">{{ getDateFromIndex(i) }}</span>
            </div>

            <v-slide-y-transition group>
              <div v-for="(course, y) of courseDay" :key="y + course._id" class="course-entry-wrapper mx-4">
                <v-card
                  @click="showDialog(course, i)"
                  class="course-card my-3"
                  :class="{
                    'signed-up': course.signedUp,
                    'is-cancelled': course.cancelledVisibility,
                    'is-instructor': course.isInstructor,
                    'pt-6': course.label && course.label.text,
                  }"
                  outlined>
                  <div v-if="course.label && course.label.text" class="course-label" :style="{ backgroundColor: course.label.color }">
                    {{ course.label.text }}
                  </div>
                  <v-chip v-if="course.cancelledVisibility" small color="red darken-2" text-color="white" class="status-chip">
                    leider abgesagt
                  </v-chip>

                  <v-list-item three-line>
                    <v-list-item-avatar size="60" class="course-avatar">
                      <lottie-player mode="normal" style="width: 52px; height: 66px" src="/img/lottiefiles/gf/dumbell.json"></lottie-player>
                    </v-list-item-avatar>

                    <v-list-item-content>
                      <v-list-item-title class="text-h6 mb-1">{{ course.course.name }}</v-list-item-title>
                      <v-list-item-subtitle v-if="course.instructors && course.instructors.length">
                        mit {{ course.instructors.map((i) => i.vorname).join(", ") }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle> {{ getCourseStartDate(course) }} - {{ getCourseEndDate(course) }} Uhr </v-list-item-subtitle>

                      <v-list-item-subtitle v-if="course.additionalInfo" class="additional-info align-start mt-2 d-flex">
                        <v-icon left small>mdi-information-outline</v-icon>
                        <span>{{ course.additionalInfo }}</span>
                      </v-list-item-subtitle>
                    </v-list-item-content>

                    <v-list-item-action class="align-self-center">
                      <v-icon>mdi-chevron-right</v-icon>
                    </v-list-item-action>
                  </v-list-item>

                  <v-chip v-if="course.onQueue" small color="warning" class="queue-chip">Warteliste</v-chip>
                </v-card>
              </div>
            </v-slide-y-transition>
          </template>
        </div>
      </div>

      <v-dialog v-model="dialog" max-width="500px" transition="dialog-bottom-transition">
        <v-card v-if="showCourse.course">
          <v-toolbar flat color="primary" dark>
            <v-toolbar-title>
              <strong>{{ showCourse.course.name }}</strong>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>

          <v-card-text class="pt-6">
            <div class="d-flex align-center mb-3">
              <v-icon class="mr-3" color="grey darken-1">mdi-calendar-clock</v-icon>
              <span>{{ getCourseStartDate(showCourse) }} - {{ getCourseEndDate(showCourse) }} Uhr</span>
            </div>

            <div v-if="showCourse.room && showCourse.room.name" class="d-flex align-center mb-3">
              <v-icon class="mr-3" color="grey darken-1">mdi-map-marker</v-icon>
              <span>{{ showCourse.room.name }}</span>
            </div>

            <div v-if="showCourse.instructors && showCourse.instructors.length" class="d-flex align-center mb-4">
              <v-icon class="mr-3" color="grey darken-1">mdi-account-circle</v-icon>
              <span>mit {{ showCourse.instructors.map((i) => i.vorname).join(", ") }}</span>
            </div>

            <p v-if="showCourse.course && showCourse.course.beschreibung" class="course-description">
              {{ showCourse.course.beschreibung }}
            </p>
          </v-card-text>

          <div v-if="clean">
            <v-card-title class="text-h6">Jetzt Anmelden</v-card-title>
            <v-card-text>Um den Kurs zu buchen, melde Dich mit Deinem roody Login an.</v-card-text>
            <v-card-actions class="px-4 pb-4">
              <v-spacer></v-spacer>
              <v-btn text @click="dialog = false">Schliessen</v-btn>
              <v-btn color="primary" depressed href="/" target="_blank">Zum Login</v-btn>
            </v-card-actions>
          </div>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import api from "../../api"
import { mapGetters, mapActions, mapState } from "vuex"
import userService from "../../services/userService"
import { jsPDF } from "jspdf"
export default {
  name: "courseListWeekly",

  data() {
    return {
      loading: true,
      clean: null,
      userService,
      searchInput: null,
      searchItems: [],
      search: [],
      isUpdating: false,
      courseList: [],
      courseListFiltered: [],
      dialog: false,
      showCourse: {},
      instructors: [],
      courses: [],
    }
  },
  async created() {
    if (this.$route.name === "courseListWeeklyClean") this.clean = true
    this.fetchCourseList()
  },
  mounted() {
    if (this.$route.params.studiocode && this.navigationVisible) this.toggleNavigationBarVisibility()
  },
  destroyed() {
    if (this.$route.params.studiocode && !this.navigationVisible) this.toggleNavigationBarVisibility()
  },
  computed: {
    ...mapState("navigationStoreNotPersisted", ["navigationVisible"]),
    alibeyStudio() {
      let studioCode = null
      if (!this.$route.params.studiocode) {
        const user = JSON.parse(localStorage.getItem("user"))
        if (user && user.user) studioCode = user.user.studio_id
      } else {
        studioCode = this.$route.params.studiocode
      }
      return studioCode && ["alibey", "ali"].includes(studioCode.toLowerCase())
    },
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["toggleNavigationBarVisibility", "setNavigationBarText"]),
    async downloadPrint() {
      try {
        let studioCode = this.$route.params.studiocode
        if (!studioCode) {
          const user = JSON.parse(localStorage.getItem("user"))
          if (user && user.user) {
            studioCode = user.user.studio_id
          }
        }
        if (studioCode) {
          await api.fetchGFWeeklyPDF(studioCode)
        }
      } catch (error) {
        console.error("Failed to download PDF:", error)
        // Optionally show a toast message to the user
      }
    },
    getDateFromIndex(index) {
      const baseDate = this.alibeyStudio ? this.$moment("2025-05-31") : this.$moment()
      return baseDate.add(index, "days").format("dddd, DD.MM.YYYY")
    },
    remove(item) {
      const index = this.search.indexOf(item.name)
      if (index >= 0) this.search.splice(index, 1)
    },
    async fetchCourseList() {
      this.loading = true
      try {
        const data = this.$route.params.studiocode ? await api.fetchExCourseList(this.$route.params.studiocode) : await api.fetchWeeklyCourseList()

        this.courseList = data.list
        this.courseListFiltered = JSON.parse(JSON.stringify(this.courseList))
        this.searchItems = [{ header: "Kurse" }, ...data.courses, { header: "Instruktoren" }, ...data.instructors]
      } catch (error) {
        console.error("Failed to fetch course list:", error)
        this.courseListFiltered = [] // Clear list on error
      } finally {
        this.loading = false
      }
    },
    getCourseStartDate(course) {
      return this.$moment().set({ hours: course.hours, minutes: course.minutes }).format("HH:mm")
    },
    getCourseEndDate(course) {
      return this.$moment().set({ hours: course.hours, minutes: course.minutes }).add(course.duration, "minutes").format("HH:mm")
    },
    showDialog(course, index) {
      if (this.$route.params.studiocode) {
        this.showCourse = course

        this.dialog = true
      } else {
        this.pushToCourse(course, index)
      }
    },
    pushToCourse(course, index) {
      if (
        !course.past ||
        course.current ||
        course.isInstructor ||
        this.userService.hasRight("gf_c_edit", "gf_c_delete", "gf_c_plan", "gf_c_create", "gf_cp_edit", "gf_f_courses")
      ) {
        const baseDate = this.alibeyStudio ? this.$moment("2025-05-31") : this.$moment()
        let ts = baseDate.add(index, "days").startOf("day").unix()

        if (course.courseTimelineId) {
          ts = 0
        }
        document.body.style = "overflow:hidden;max-height:100vh;"
        this.$router.push({ name: "courseListView", params: { id: course._id, ts } })
      }
    },
  },
  watch: {
    search(val) {
      if (!val || val.length === 0) {
        this.courseListFiltered = this.courseList
        return
      }

      const courseList = this.courseList.map((day) => {
        if (!day) return null
        const filteredDay = day.filter((course) => {
          if (!course.searchText) return false
          return val.some((searchTerm) => course.searchText.includes(searchTerm))
        })
        return filteredDay.length > 0 ? filteredDay : null
      })
      this.courseListFiltered = courseList
    },
  },
}
</script>

<style lang="scss" scoped>
/* NEU: Zusätzliche Styles für die neuen Elemente */
.additional-info {
  font-style: italic;
  font-size: 0.85rem !important;
}

.course-label {
  position: absolute;
  top: -5px;
  width: 100%;
  left: 0px;
  padding: 4px 10px 4px 12px;
  color: white;
  font-weight: 700;
  font-size: 0.75rem;
  text-align: center;
  text-transform: uppercase;
  z-index: 3; // Höher als der Status-Chip
}

// ===================================================================
//  1. ALLGEMEINES LAYOUT & SEITEN-HINTERGRUND
// ===================================================================
.weekly-view-wrapper {
  //  background-color: #f7f8fa;
  min-height: 100vh;
  padding-bottom: 80px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;

  .weekly-view-inner-wrapper {
    max-width: 500px;
    margin: 0px auto;
  }
}

// ===================================================================
//  2. FILTER & CONTROLS
// ===================================================================
.filter-controls-container {
  //  background-color: #f7f8fa;
  border-bottom: 1px solid #ededf0;

  top: 0;
  z-index: 10;
}

::v-deep .v-text-field--outlined .v-input__control {
  border-radius: 12px;
}

// ===================================================================
//  3. DAS NEUE, SOLIDE KARTEN-DESIGN
// ===================================================================
.course-card.is-cancelled {
  opacity: 0.6;
  background-color: #f5f5f5 !important;
}

.status-chip {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  font-weight: bold;
}

.course-card {
  width: 100%;
  background: #ffffff !important;
  border-radius: 24px !important;
  border: 1px solid #ededf0;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 20, 50, 0.05), 0 10px 30px rgba(0, 20, 50, 0.07);
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 6px 16px rgba(0, 20, 50, 0.08), 0 15px 40px rgba(0, 20, 50, 0.1);
  }

  &.signed-up {
    border: 2px solid var(--v-primary-base, #00963f);
  }

  &.is-instructor {
    border: 2px solid #f57c00; // Distinct orange border for instructors
  }
}

// ===================================================================
//  4. POLIERTE UI-ELEMENTE & TYPOGRAFIE
// ===================================================================
.v-list-item-title {
  font-weight: 700;
  color: #1a232e !important;
  font-size: 1.1rem;
}

.v-list-item-subtitle {
  color: #5a6470 !important;
  font-size: 0.9rem;
}

.course-avatar {
  background-color: #f7f8fa;
  border-radius: 18px;
  border: 1px solid #ededf0;
}

.day-divider {
  padding: 28px 16px 12px;
  .day-chip {
    background-color: #e9ebf0;
    color: #5a6470;
    font-weight: 600;
    font-size: 0.85rem;
    padding: 8px 16px;
    border-radius: 20px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.queue-chip {
  position: absolute;
  bottom: 12px;
  right: 16px;
  border-radius: 8px;
  font-weight: 600;
  z-index: 2;
}

.course-description {
  max-height: 120px;
  overflow-y: auto;
  white-space: pre-wrap;
  color: #5a6470;
  font-size: 0.95rem;
  line-height: 1.6;
}
</style>
